import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import CancelOutlined from '@mui/icons-material/CancelOutlined';
import Check from '@mui/icons-material/Check';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import MailOutline from '@mui/icons-material/MailOutline';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import LogoutOutlined from '@mui/icons-material/LogoutOutlined';
import LoginOutlined from '@mui/icons-material/LoginOutlined';
import { Button } from '@practium/ui';
import { useAuth } from '@practium/features/auth';
import { useSyncReceiptUrl } from '@practium/features/api';
import {
	PaymentResultLayout,
	PaymentResultIcon,
	PaymentResultLoading,
	PaymentDetailsCard,
	PaymentNoticeBanner,
} from '../components/PaymentResult';
import type { PaymentResultDetails } from '../components/PaymentResult';
import { usePaymentOutcome } from '../hooks/usePaymentOutcome';
import { usePaymentResultReveal } from '../hooks/usePaymentResultReveal';
import { formatIntervalAdjective } from '../planFormatters';
import { markPaymentCelebrationPending } from '../../../shared/utils/paymentCelebration';
import styles from './PaymentSuccessPageClient.module.css';

const PAYMENT_METHOD_LABELS: Record<string, string> = {
	credit_card: 'Cartão de Crédito',
	boleto: 'Boleto Bancário',
};

export const PaymentSuccessPageClient = () => {
	const navigate = useNavigate();
	const { logout } = useAuth();
	const { me, subscription, paymentStatus, isResolving, timedOut, isError, isSessionError, checkAgain, refetch } =
		usePaymentOutcome();
	const isTerminal = !isResolving && paymentStatus === 'paid' && !!subscription && !!me;
	const { phase, landed, standaloneIconRef, finalIconRef } = usePaymentResultReveal(isTerminal);
	const { mutate: syncReceiptUrl } = useSyncReceiptUrl();
	const receiptSyncAttemptedRef = useRef(false);

	const handleLogout = () => {
		logout().then(() => navigate('/login', { replace: true }));
	};

	useEffect(() => {
		if (!isTerminal || receiptSyncAttemptedRef.current || me?.gatewayMetadata?.receiptUrl) return;
		receiptSyncAttemptedRef.current = true;
		syncReceiptUrl(undefined, { onSuccess: () => refetch() });
	}, [isTerminal, me, syncReceiptUrl, refetch]);

	const handleExplore = () => {
		markPaymentCelebrationPending();
		navigate('/');
	};

	if (isResolving) {
		return <PaymentResultLoading ref={standaloneIconRef} tone="success" spinning />;
	}

	if (paymentStatus === 'failed') {
		navigate('/billing/error', { replace: true });
		return null;
	}

	if (isTerminal && phase !== 'revealed') {
		return (
			<PaymentResultLoading
				ref={standaloneIconRef}
				tone="success"
				spinning={false}
				icon={phase === 'morphed' ? <Check /> : undefined}
			/>
		);
	}

	if (!isTerminal) {
		const showError = isError || isSessionError;
		return (
			<PaymentResultLayout
				tone={showError ? 'error' : 'success'}
				header={
					<>
						<PaymentResultIcon
							icon={showError ? <CancelOutlined /> : <CheckCircleOutlined />}
							tone={showError ? 'error' : 'success'}
						/>
						<div className={styles.headingGroupSpaced}>
							<h1 className={`${styles.title} ${showError ? styles.titleError : ''}`}>
								{isSessionError
									? 'Sua sessão expirou'
									: isError
										? 'Não foi possível verificar seu pagamento'
										: 'Pagamento em processamento'}
							</h1>
							<p className={styles.subtitle}>
								{isSessionError
									? 'Entre novamente para confirmarmos seu pagamento. O pagamento no gateway já pode ter sido concluído.'
									: isError
										? 'Não conseguimos consultar o status do seu pagamento agora. Verifique sua conexão e tente novamente.'
										: timedOut
											? 'Ainda estamos confirmando seu pagamento junto ao gateway. Isso pode levar alguns minutos, você receberá um e-mail assim que for concluído.'
											: 'Estamos confirmando seu pagamento. Aguarde um instante.'}
							</p>
						</div>
					</>
				}
				content={null}
				footer={
					<div className={styles.actions}>
						{isSessionError ? (
							<Button
								variant="primary"
								fullWidth
								icon={<LoginOutlined />}
								className={`${styles.primaryButton} ${styles.primaryButtonError}`}
								onClick={() => navigate('/login', { replace: true })}
							>
								Entrar novamente
							</Button>
						) : (
							<Button
								variant="primary"
								fullWidth
								icon={<RefreshOutlined />}
								className={`${styles.primaryButton} ${isError ? styles.primaryButtonError : ''}`}
								onClick={checkAgain}
							>
								Verificar Novamente
							</Button>
						)}
						<Button
							variant="outline"
							fullWidth
							icon={<LogoutOutlined />}
							className={styles.secondaryButton}
							onClick={handleLogout}
						>
							Sair do Sistema
						</Button>
					</div>
				}
			/>
		);
	}

	const plan = subscription.plan;
	const receiptUrl = me.gatewayMetadata?.receiptUrl;
	const details: PaymentResultDetails = {
		amount: plan?.price ?? 0,
		paymentMethod: PAYMENT_METHOD_LABELS[subscription.paymentMethod] ?? subscription.paymentMethod ?? '—',
		planName: plan ? `${plan.name} · ${formatIntervalAdjective(plan.interval, plan.intervalCount)}` : '—',
		date: new Date(subscription.updatedAt ?? subscription.signedAt).toLocaleString('pt-BR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		}),
		customerName: `${me.name ?? ''} ${me.surName ?? ''}`.trim(),
		customerDocument: me.document ?? '',
		companyName: '',
		companyDocument: '',
	};

	return (
		<PaymentResultLayout
			header={
				<>
					<PaymentResultIcon
						ref={finalIconRef}
						icon={<Check />}
						tone="success"
						animateReveal={false}
						bare={!landed}
						ringed
					/>
					<div className={styles.headingGroup}>
						<h1 className={styles.title}>Pagamento Realizado!</h1>
						<p className={styles.subtitle}>
							Seu pagamento foi processado com sucesso. Você receberá uma confirmação no seu e-mail
							em breve.
						</p>
					</div>
				</>
			}
			content={
				<>
					<PaymentDetailsCard details={details} />
					<PaymentNoticeBanner icon={<MailOutline />} tone="info">
						Confirmação enviada para {me.email}
					</PaymentNoticeBanner>
				</>
			}
			footer={
				<>
					<div className={styles.actions}>
						<Button
							variant="primary"
							fullWidth
							icon={<ArrowBackOutlined />}
							className={styles.primaryButton}
							onClick={handleExplore}
						>
							Explorar o Practium
						</Button>
						{receiptUrl && (
							<Button
								variant="outline"
								fullWidth
								icon={<ReceiptLongOutlined />}
								className={styles.secondaryButton}
								onClick={() => globalThis.open(receiptUrl, '_blank', 'noopener,noreferrer')}
							>
								Ver Comprovante
							</Button>
						)}
					</div>
					<p className={styles.helpText}>
						Precisa de ajuda? Entre em contato com suporte@practium.com
					</p>
				</>
			}
		/>
	);
};
